import React, { useState } from 'react';
import { Shield, Copy, CheckCircle, Info } from 'lucide-react';

export const GoogleRecovery: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [copied, setCopied] = useState(false);

  const txtRecordValue = 'google-site-verification=abcd1234567890example';
  const txtRecordName = '@';

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Add Google Recovery TXT Record</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Add Google account recovery TXT records to help users recover their Google Workspace accounts
        </p>
      </div>

      <div className="card-modern p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-4">Domain Information</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Domain Name
              </label>
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className="input-modern h-10 w-full px-3 text-sm"
                placeholder="example.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Recovery Email Address
              </label>
              <input
                type="email"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                className="input-modern h-10 w-full px-3 text-sm"
                placeholder="admin@example.com"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h2 className="text-lg font-semibold mb-4">TXT Record Details</h2>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Record Name
              </label>
              <div className="flex items-center space-x-2">
                <div className="flex-1 p-3 bg-muted rounded-lg font-mono text-sm">
                  {txtRecordName}
                </div>
                <button
                  onClick={() => copyToClipboard(txtRecordName)}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                Record Value
              </label>
              <div className="flex items-center space-x-2">
                <div className="flex-1 p-3 bg-muted rounded-lg font-mono text-sm break-all">
                  {txtRecordValue}
                </div>
                <button
                  onClick={() => copyToClipboard(txtRecordValue)}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                TTL (Time to Live)
              </label>
              <select className="input-modern h-10 w-full px-3 text-sm">
                <option value="auto">Auto (Recommended)</option>
                <option value="300">5 minutes</option>
                <option value="3600">1 hour</option>
                <option value="86400">24 hours</option>
              </select>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h2 className="text-lg font-semibold mb-4">DNS Provider</h2>
          <div className="grid gap-3 md:grid-cols-3">
            <label className="card-modern p-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary">
              <input type="radio" name="provider" value="cloudflare" className="sr-only" />
              <div className="text-center">
                <Shield className="h-6 w-6 mx-auto mb-2 text-primary" />
                <p className="text-sm font-medium">Cloudflare</p>
              </div>
            </label>
            <label className="card-modern p-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary">
              <input type="radio" name="provider" value="route53" className="sr-only" />
              <div className="text-center">
                <Shield className="h-6 w-6 mx-auto mb-2 text-primary" />
                <p className="text-sm font-medium">Route 53</p>
              </div>
            </label>
            <label className="card-modern p-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary">
              <input type="radio" name="provider" value="other" className="sr-only" />
              <div className="text-center">
                <Shield className="h-6 w-6 mx-auto mb-2 text-primary" />
                <p className="text-sm font-medium">Other</p>
              </div>
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <button className="btn-primary px-6 py-2 rounded-lg font-medium">
            Add TXT Record
          </button>
        </div>
      </div>

      <div className="card-modern p-4 bg-blue-500/10 border border-blue-500/20">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-600 dark:text-blue-400">About Google Recovery Records</h3>
            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
              These TXT records help Google verify domain ownership for account recovery purposes. 
              They enable administrators to recover access to Google Workspace accounts associated with the domain.
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
              After adding the record, it may take up to 48 hours for DNS propagation to complete.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 